import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/page-header";
import { SecurityFormModal } from "@/components/securities/security-form-modal";
import { apiRequest } from "@/lib/queryClient";
import { getSecurityDisplayName } from "@/lib/security-display-name";
import { Link } from "wouter";

// Security types from schema (matches enum security_type_enum in database)
type SecurityType = "residential_property" | "commercial_property" | "vehicle" | "equipment" | "financial_asset";

// Security status from schema (matches enum security_status_enum - 9 values)
type SecurityStatus = "identified" | "proposed" | "valued" | "approved" | "registered" | "varied" | "substituted" | "discharged" | "cancelled";

interface Security {
  id: string;
  securityType: SecurityType;
  collateralType: string | null;
  status: SecurityStatus;
  estimatedValue: string;
  valuationDate: string | null;
  valuationSource: string | null;
  description: string | null;
  isExistingSecurity: boolean;
  propertyAddress?: string | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleRegistrationNumber?: string | null;
  equipmentMake?: string | null;
  equipmentModel?: string | null;
  equipmentSerialNumber?: string | null;
  equipmentAssetTag?: string | null;
  financialAssetDescription?: string | null;
  financialAssetType?: string | null;
  financialAssetInstitutionName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function SecuritiesList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const { data, isLoading } = useQuery<Security[]>({
    queryKey: ["/api/crm/securities", searchTerm, typeFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("includeProperty", "true");
      if (searchTerm) params.append("search", searchTerm);
      if (typeFilter !== "all") params.append("securityType", typeFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);

      const response = await apiRequest("GET", `/api/crm/securities?${params.toString()}`);
      return response.json();
    },
  });

  const securities = Array.isArray(data) ? data : [];

  const securityTypes = [
    { value: "all", label: "All Types" },
    { value: "residential_property", label: "Residential Property" },
    { value: "commercial_property", label: "Commercial Property" },
    { value: "vehicle", label: "Vehicle" },
    { value: "equipment", label: "Equipment" },
    { value: "financial_asset", label: "Financial Asset" },
  ];

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "identified", label: "Identified" },
    { value: "proposed", label: "Proposed" },
    { value: "valued", label: "Valued" },
    { value: "approved", label: "Approved" },
    { value: "registered", label: "Registered" },
    { value: "varied", label: "Varied" },
    { value: "substituted", label: "Substituted" },
    { value: "discharged", label: "Discharged" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const getStatusBadgeColor = (status: SecurityStatus) => {
    switch (status) {
      case "identified":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
      case "proposed":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "valued":
        return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200";
      case "approved":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200";
      case "registered":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "varied":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200";
      case "substituted":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      case "discharged":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatSecurityType = (type: SecurityType) => {
    const labels: Record<SecurityType, string> = {
      residential_property: "Residential Property",
      commercial_property: "Commercial Property",
      vehicle: "Vehicle",
      equipment: "Equipment",
      financial_asset: "Financial Asset",
    };
    return labels[type] || type;
  };

  const formatStatus = (status: SecurityStatus) => {
    const labels: Record<SecurityStatus, string> = {
      identified: "Identified",
      proposed: "Proposed",
      valued: "Valued",
      approved: "Approved",
      registered: "Registered",
      varied: "Varied",
      substituted: "Substituted",
      discharged: "Discharged",
      cancelled: "Cancelled",
    };
    return labels[status] || status;
  };

  const formatCurrency = (value: string | null) => {
    if (!value) return "-";
    const num = parseFloat(value);
    if (isNaN(num)) return "-";
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatDate = (value: string | null) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Securities"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Securities" }]}
        actions={
          <Button
            data-testid="button-add-security"
            onClick={() => setCreateModalOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Security
          </Button>
        }
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-4">
        <div className="mx-auto max-w-7xl space-y-4">
          {/* Search and Filters */}
          <Card className="p-3">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search securities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="input-search-securities"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                    data-testid="button-clear-search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex gap-2">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Security Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {securityTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {/* Securities Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Estimated Value</TableHead>
                  <TableHead>Valuation Date</TableHead>
                  <TableHead>Existing Security</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center">
                      Loading securities...
                    </TableCell>
                  </TableRow>
                ) : securities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center">
                      No securities found
                    </TableCell>
                  </TableRow>
                ) : (
                  securities.map((security) => (
                    <TableRow key={security.id}>
                      <TableCell className="font-medium">
                        {formatSecurityType(security.securityType)}
                      </TableCell>
                      <TableCell>
                        <Link href={`/securities/${security.id}`} className="text-blue-600 hover:underline">
                          {getSecurityDisplayName(security)}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {security.propertyAddress || "-"}
                      </TableCell>
                      <TableCell>{formatCurrency(security.estimatedValue)}</TableCell>
                      <TableCell>{formatDate(security.valuationDate)}</TableCell>
                      <TableCell>
                        {security.isExistingSecurity ? (
                          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                            Yes
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                            No
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(security.status)}>
                          {formatStatus(security.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Link href={`/securities/${security.id}`} className="text-sm text-blue-600 hover:underline">
                          View
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      </main>

      {/* Create Security Modal */}
      <SecurityFormModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />
    </div>
  );
}
